import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Pagination, Alert } from 'react-bootstrap';
import { FaPlus, FaSearch, FaEdit, FaEye, FaTrash, FaFileDownload } from 'react-icons/fa';
import api from '../../services/api';
import { toast } from 'react-toastify';
import Spinner from '../../components/layout/Spinner';

const PrescriptionList = () => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('prescriptionDate');
  const [sortOrder, setSortOrder] = useState('desc');
  const [customerFilter, setCustomerFilter] = useState('');
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    fetchPrescriptions();
    fetchCustomers();
  }, [currentPage, sortField, sortOrder, customerFilter]);

  const fetchPrescriptions = async (search = searchTerm) => {
    try {
      setLoading(true);
            const res = await api.get('/prescriptions', {
        params: {
          page: currentPage,
          limit: 10,
          search,
          sortField,
          sortOrder,
          customer: customerFilter
        }
      });
      
      setPrescriptions(res.data.prescriptions);
      setTotalPages(res.data.pagination.pages);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching prescriptions:', err);
      setError('Failed to load prescriptions');
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
            const res = await api.get('/customers', {
        params: {
          limit: 100, // Get a reasonable number of customers for the filter
          sortField: 'name',
          sortOrder: 'asc'
        }
      });
      setCustomers(res.data.customers);
    } catch (err) {
      console.error('Error fetching customers for filter:', err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchPrescriptions(searchTerm);
  };

  const handleSort = (field) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleCustomerFilterChange = (e) => {
    setCustomerFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDownload = async (id, fileName) => {
    try {
            const response = await api.get(`/prescriptions/download/${id}`, {
        responseType: 'blob'
      });
      
      // Create a blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName || 'prescription.pdf');
      
      // Append to html page
      document.body.appendChild(link);
      
      // Force download
      link.click();
      
      // Clean up and remove the link
      link.parentNode.removeChild(link);
    } catch (err) {
      console.error('Error downloading prescription:', err);
      toast.error('Failed to download prescription');
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }
    
    // Previous button
    pages.push(
      <Pagination.Item 
        key="prev" 
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
      >
        &laquo;
      </Pagination.Item>
    );
    
    // First page
    if (startPage > 1) {
      pages.push(
        <Pagination.Item 
          key={1} 
          active={1 === currentPage}
          onClick={() => handlePageChange(1)}
        >
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        pages.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }
    
    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item 
          key={i} 
          active={i === currentPage}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    
    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      pages.push(
        <Pagination.Item 
          key={totalPages} 
          active={totalPages === currentPage}
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }
    
    // Next button
    pages.push(
      <Pagination.Item 
        key="next" 
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        &raquo;
      </Pagination.Item>
    );
    
    return <Pagination>{pages}</Pagination>;
  };

  if (loading && prescriptions.length === 0) {
    return <Spinner />;
  }

  return (
    <Container>
      <Row className="mb-4 align-items-center">
        <Col>
          <h1>Prescriptions</h1>
        </Col>
        <Col xs="auto">
          <Button as={Link} to="/prescriptions/new" variant="primary">
            <FaPlus className="me-2" /> Add New Prescription
          </Button>
        </Col>
      </Row>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={8}>
              <Form onSubmit={handleSearch}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Search by doctor name or prescription type"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button type="submit" variant="primary">
                    <FaSearch /> Search
                  </Button>
                </InputGroup>
              </Form>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Select 
                  value={customerFilter} 
                  onChange={handleCustomerFilterChange}
                >
                  <option value="">All Customers</option>
                  {customers.map(customer => (
                    <option key={customer._id} value={customer._id}>
                      {customer.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : prescriptions.length === 0 ? (
            <div className="text-center py-4">
              <p className="mb-3">No prescriptions found.</p>
              <Button as={Link} to="/prescriptions/new" variant="primary">
                Add New Prescription
              </Button>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table hover>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('prescriptionDate')} style={{ cursor: 'pointer' }}>
                        Date {renderSortIcon('prescriptionDate')}
                      </th>
                      <th>Customer</th>
                      <th>Doctor</th>
                      <th>Type</th>
                      <th onClick={() => handleSort('expiryDate')} style={{ cursor: 'pointer' }}>
                        Expiry {renderSortIcon('expiryDate')}
                      </th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prescriptions.map((prescription) => (
                      <tr key={prescription._id}>
                        <td>{new Date(prescription.prescriptionDate).toLocaleDateString()}</td>
                        <td>
                          <Link to={`/customers/${prescription.customer._id}`}>
                            {prescription.customer.name}
                          </Link>
                        </td>
                        <td>{prescription.doctorName}</td>
                        <td>{prescription.prescriptionType}</td>
                        <td>
                          {prescription.expiryDate ? 
                            new Date(prescription.expiryDate).toLocaleDateString() : 
                            'N/A'}
                        </td>
                        <td className="text-center">
                          <Button
                            as={Link}
                            to={`/prescriptions/${prescription._id}`}
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            title="View Prescription"
                          >
                            <FaEye />
                          </Button>
                          <Button
                            as={Link}
                            to={`/prescriptions/edit/${prescription._id}`}
                            variant="outline-secondary"
                            size="sm"
                            className="me-1"
                            title="Edit Prescription"
                          >
                            <FaEdit />
                          </Button>
                          {prescription.prescriptionImage && (
                            <Button
                              variant="outline-info"
                              size="sm"
                              className="me-1"
                              title="Download Prescription"
                              onClick={() => handleDownload(prescription._id, `prescription-${prescription._id}.pdf`)}
                            >
                              <FaFileDownload />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              
              <div className="d-flex justify-content-center mt-4">
                {renderPagination()}
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PrescriptionList;
